import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UseRowDrawerResult } from './useRowDrawer';

type RowDrawerFooterProps = Pick<
    UseRowDrawerResult,
    'canSave' | 'onSave' | 'onClose' | 'onToggleExclusion'
> & {
    /** The row is excluded now: the first button includes it back. */
    isExcluded: boolean;
};

/**
 * The row drawer's pinned footer (`RowDrawerView`): Exclude row / Include
 * row (danger-soft) at the start, Cancel and **Save and re-check** at the
 * end. On a phone Save takes the first line on its own and the other two
 * share the second. The 560 px drawer leaves the row 520 px: in Georgian
 * the three fit with the exclusion button's short label ("გამორიცხვა");
 * anything longer wraps, Save last.
 */
export const RowDrawerFooter: React.FC<RowDrawerFooterProps> = ({
    canSave,
    isExcluded,
    onSave,
    onClose,
    onToggleExclusion,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
            <Button
                className="w-full sm:me-auto sm:w-auto"
                onPress={onToggleExclusion}
                variant="danger-soft"
                {...getNodeTestIdProp('catalog-import-row-drawer-exclusion')}
            >
                {t(
                    isExcluded
                        ? 'catalogImport.products.drawer.actions.include'
                        : 'catalogImport.products.drawer.actions.exclude',
                )}
            </Button>
            <Button
                className="w-full sm:w-auto"
                onPress={onClose}
                variant="tertiary"
                {...getNodeTestIdProp('catalog-import-row-drawer-cancel')}
            >
                {t('catalogImport.products.drawer.actions.cancel')}
            </Button>
            <Button
                className="col-span-2 w-full max-sm:order-first sm:w-auto"
                isDisabled={!canSave}
                onPress={onSave}
                variant="primary"
                {...getNodeTestIdProp('catalog-import-row-drawer-save')}
            >
                {t('catalogImport.products.drawer.actions.save')}
            </Button>
        </div>
    );
};
